import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.jsx";
import { Badge } from "../ui/badge.jsx";
import { Skeleton } from "../ui/skeleton.jsx";
import { Card, CardContent } from "../ui/card.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.jsx";
import { Button } from "../ui/button.jsx";
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Clock, 
  AlertTriangle, 
  Eye, 
  Building2, 
  User,
  Calendar,
  MessageSquare,
  Star,
  Zap,
  ArrowUpCircle,
  Circle,
  CheckCircle2,
  MoreVertical,
  Edit2,
  Trash2,
  UserCheck,
  UserX,
  X,
  AlertCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from "../ui/dropdown-menu.jsx";
import { getTimeElapsedString } from '../../utils/dateHelpers.js';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../ui/dialog.jsx';
import { Input } from '../ui/input.jsx';

const STATUS_CONFIG = {
  open: {
    color: 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300',
    label: 'Aberto',
    icon: AlertTriangle
  },
  analysis: {
    color: 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border-purple-300',
    label: 'Em Análise',
    icon: Clock
  },
  in_progress: {
    color: 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border-blue-300',
    label: 'Em Andamento',
    icon: Clock
  },
  resolved: {
    color: 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-300',
    label: 'Resolvido',
    icon: CheckCircle2
  },
  closed: {
    color: 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border-gray-300',
    label: 'Encerrado',
    icon: CheckCircle2
  },
  reopened: {
    color: 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border-purple-300',
    label: 'Reaberto',
    icon: AlertTriangle
  }
};

const PRIORITY_CONFIG = {
  low: {
    color: 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border-blue-300',
    label: 'Baixa',
    icon: Circle
  },
  medium: {
    color: 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border-yellow-300',
    label: 'Média',
    icon: ArrowUpCircle
  },
  high: {
    color: 'bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border-orange-300',
    label: 'Alta',
    icon: AlertTriangle
  },
  critical: {
    color: 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300',
    label: 'Crítica',
    icon: Zap
  }
};

// Cores para tipos de chamado
const TYPE_CONFIG = {
  support: {
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    label: 'Suporte',
  },
  development: {
    color: 'bg-green-100 text-green-800 border-green-300',
    label: 'Desenvolvimento',
  },
  project: {
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    label: 'Projeto',
  },
  geral: {
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    label: 'Geral',
  },
};

// Função utilitária para badge de alerta inteligente
function getAlertBadge(createdAt, status) {
  if (!(['open','analysis','in_progress','reopened'].includes(status))) return null;
  const created = new Date(createdAt);
  const now = new Date();
  const diff = (now - created) / (1000 * 60 * 60); // horas
  const diffDays = diff / 24;
  if (diffDays > 30) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-black text-white text-xs animate-pulse font-bold">Extremo: +30 dias</span>;
  }
  if (diffDays > 20) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-blue-900 text-white text-xs animate-pulse font-bold">Crítico: +20 dias</span>;
  }
  if (diffDays > 10) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-purple-700 text-white text-xs animate-pulse font-bold">Urgente: +10 dias</span>;
  }
  if (diff > 48) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-red-500 text-white text-xs animate-pulse font-bold">Alerta: +48h</span>;
  }
  if (diff > 24) {
    return <span className="ml-2 px-2 py-1 rounded-full bg-yellow-400 text-white text-xs animate-pulse font-bold">Atenção: +24h</span>;
  }
  return null;
}

export default function TicketList({ tickets, companies, clients = [], users = [], isLoading, isInternalUser, onAssign }) {
  const navigate = useNavigate();
  // Estado de ordenação
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingTicket, setPendingTicket] = useState(null);
  const [reason, setReason] = useState('');

  // Função para ordenar os tickets
  const sortedTickets = [...tickets];
  if (sortConfig.key) {
    sortedTickets.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      // Para datas, converter para Date
      if (sortConfig.key === 'created_at' || sortConfig.key === 'created_date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Função para alternar ordenação
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Função para obter o nome do cliente/empresa
  const getClientCompanyName = (ticket) => {
    // Primeiro tenta buscar pelo client_id
    if (ticket.client_id) {
      const client = clients.find(c => c.id === ticket.client_id);
      if (client) {
        return client.name || client.corporate_name || 'Cliente';
      }
    }
    
    // Se não encontrar cliente, busca pela empresa
    if (ticket.company_id) {
      const company = companies.find(c => c.id === ticket.company_id);
      if (company) {
        return company.name || company.corporate_name || 'Empresa';
      }
    }
    
    return 'Cliente não encontrado';
  };

  // Função para obter usuário responsável
  const getAssignedUser = (userId) => {
    return users.find(u => u.id === userId);
  };

  // Função para formatar data segura
  const formatDateSafely = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '--:--' };
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      if (!isValid(date)) return { date: 'Data inválida', time: '--:--' };
      
      return {
        date: format(date, "dd/MM/yyyy", { locale: ptBR }),
        time: format(date, "HH:mm", { locale: ptBR })
      };
    } catch {
      return { date: 'Data inválida', time: '--:--' };
    }
  };

  // Função para verificar se está vencido
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    try {
      const date = typeof dueDate === 'string' ? parseISO(dueDate) : new Date(dueDate);
      return isValid(date) && date < new Date();
    } catch {
      return false;
    }
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/ViewTicket?id=${ticketId}`);
  };

  // Função para atualizar status do chamado
  const handleStatusUpdate = async (ticket, status) => {
    if ((status === 'resolved' || status === 'cancelled')) {
      setPendingStatus(status);
      setPendingTicket(ticket);
      setShowReasonModal(true);
      return;
    }
    await onAssign(ticket.id, status);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (tickets.length === 0) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="text-center">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum chamado encontrado</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {isInternalUser 
                ? 'Não há chamados que correspondam aos filtros selecionados.' 
                : 'Você ainda não tem chamados registrados.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border-0">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-150">
                <TableHead className="font-semibold text-gray-700 cursor-pointer select-none" onClick={() => handleSort('ticket_number')}>ID {sortConfig.key === 'ticket_number' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</TableHead>
                <TableHead className="font-semibold text-gray-700 cursor-pointer select-none" onClick={() => handleSort('title')}>Título {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</TableHead>
                {isInternalUser && <TableHead className="font-semibold text-gray-700 cursor-pointer select-none" onClick={() => handleSort('client_id')}>Cliente {sortConfig.key === 'client_id' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</TableHead>}
                <TableHead className="font-semibold text-gray-700 cursor-pointer select-none" onClick={() => handleSort('created_by')}>Criado por {sortConfig.key === 'created_by' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</TableHead>
                <TableHead className="font-semibold text-gray-700 cursor-pointer select-none" onClick={() => handleSort('assigned_to')}>Responsável {sortConfig.key === 'assigned_to' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</TableHead>
                <TableHead className="font-semibold text-gray-700 cursor-pointer select-none" onClick={() => handleSort('priority')}>Prioridade {sortConfig.key === 'priority' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</TableHead>
                <TableHead className="font-semibold text-gray-700 cursor-pointer select-none" onClick={() => handleSort('status')}>Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</TableHead>
                <TableHead className="font-semibold text-gray-700 cursor-pointer select-none" onClick={() => handleSort('created_at')}>Abertura {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</TableHead>
                <TableHead className="font-semibold text-gray-700">Tempo em Aberto</TableHead>
                <TableHead className="font-semibold text-gray-700 text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTickets.map((ticket) => {
                const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
                const StatusIcon = statusConfig.icon;
                const PriorityIcon = priorityConfig.icon;
                const dateInfo = formatDateSafely(ticket.created_at || ticket.created_date);
                const overdue = isOverdue(ticket.due_date);
                const client = clients.find(c => c.id === ticket.client_id);

                // Badge de tipo de chamado com cor
                const typeConfig = TYPE_CONFIG[ticket.type] || TYPE_CONFIG.geral;

                return (
                  <TableRow 
                    key={ticket.id}
                    className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-200 cursor-pointer group"
                    onClick={() => handleTicketClick(ticket.id)}
                  >
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">#{ticket.ticket_number || ticket.id?.slice(0, 8)}</span>
                        {ticket.is_favorite && (
                          <Star className="h-3 w-3 text-yellow-500 fill-current" />
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell className="max-w-xs">
                      <div className="space-y-1">
                        <div className="font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {ticket.title}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {ticket.type && (
                            <Badge variant="outline" className={`text-xs px-1 py-0 border ${typeConfig.color}`}>
                              {typeConfig.label}
                            </Badge>
                          )}
                          {overdue && (
                            <div className="flex items-center text-red-500 gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              <span>Vencido</span>
                            </div>
                          )}
                          {(ticket.interactions?.length > 0 || ticket.comments_count > 0) && (
                            <div className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              <span>{ticket.interactions?.length || ticket.comments_count || 0}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    
                    {isInternalUser && (
                      <TableCell>
                        <div className="flex items-center gap-2 max-w-xs">
                          <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                          <span className="truncate text-sm">{getClientCompanyName(ticket)}</span>
                        </div>
                      </TableCell>
                    )}

                    <TableCell>
                      {ticket.creator_name && ticket.creator_name !== 'Usuário Desconhecido' ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={ticket.creator_avatar_url} alt={ticket.creator_name} />
                            <AvatarFallback className="text-xs bg-primary/10 text-primary">
                              {(ticket.creator_name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm truncate max-w-[100px]">{ticket.creator_name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-500">
                          <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center">
                            <User className="h-3 w-3" />
                          </div>
                          <span className="text-sm">Usuário Desconhecido</span>
                        </div>
                      )}
                    </TableCell>

                    <TableCell>
                      {ticket.assigned_name && ticket.assigned_name !== 'Usuário Desconhecido' ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={ticket.assigned_avatar_url} alt={ticket.assigned_name} />
                            <AvatarFallback className="text-xs bg-primary/10 text-primary">
                              {(ticket.assigned_name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm truncate max-w-[100px]">{ticket.assigned_name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-500">
                          <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center">
                            <User className="h-3 w-3" />
                          </div>
                          <span className="text-sm">Não atribuído</span>
                        </div>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <Badge className={`${priorityConfig.color} flex items-center gap-1 w-fit`}>
                        <PriorityIcon className="h-3 w-3" />
                        {priorityConfig.label}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          {dateInfo.date}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {dateInfo.time}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{getTimeElapsedString(ticket.created_at || ticket.created_date)}</span>
                        {getAlertBadge(ticket.created_at || ticket.created_date, ticket.status)}
                      </div>
                    </TableCell>

                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={e => { e.stopPropagation(); handleTicketClick(ticket.id); }}>
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/EditTicket?id=${ticket.id}`); }}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {!(['resolved', 'closed'].includes(ticket.status)) ? (
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger>
                                <UserCheck className="h-4 w-4 mr-2" />
                                <span>Responsável</span>
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent className="min-w-[220px]">
                                <DropdownMenuRadioGroup value={ticket.assigned_to || ""} onValueChange={value => onAssign(ticket.id, value || null)}>
                                  <DropdownMenuRadioItem value="" onClick={e => e.stopPropagation()}>
                                    <div className="flex items-center">
                                      <UserX className="h-4 w-4 mr-2 text-muted-foreground" />
                                      <span>Não atribuído</span>
                                    </div>
                                  </DropdownMenuRadioItem>
                                  <DropdownMenuSeparator />
                                  {users.map(user => (
                                    <DropdownMenuRadioItem key={user.id} value={user.id} onClick={e => e.stopPropagation()}>
                                      <div className="flex items-center">
                                        <Avatar className="h-5 w-5 mr-2">
                                          <AvatarImage src={user.avatar_url} alt={user.name} />
                                          <AvatarFallback className="text-[10px]">
                                            {(user.name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                                          </AvatarFallback>
                                        </Avatar>
                                        <span className="truncate">{user.name}</span>
                                      </div>
                                    </DropdownMenuRadioItem>
                                  ))}
                                </DropdownMenuRadioGroup>
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          ) : (
                            <DropdownMenuItem disabled>
                              <UserCheck className="h-4 w-4 mr-2 text-muted-foreground" />
                              <span className="text-muted-foreground">Responsável (ticket concluído)</span>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {!(['resolved', 'closed'].includes(ticket.status)) && (
                            <>
                              <DropdownMenuItem onClick={e => { e.stopPropagation(); handleStatusUpdate(ticket, 'resolved'); }}>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Concluir
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={e => { e.stopPropagation(); handleStatusUpdate(ticket, 'cancelled'); }}>
                                <X className="h-4 w-4 mr-2" />
                                Cancelar
                              </DropdownMenuItem>
                            </>
                          )}
                          {(['resolved', 'closed'].includes(ticket.status)) && (
                            <DropdownMenuItem disabled>
                              <AlertCircle className="h-4 w-4 mr-2" />
                              Ticket concluído
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-red-600" onClick={e => { e.stopPropagation(); onDelete(ticket.id); }}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      {showReasonModal && (
        <Dialog open={showReasonModal} onOpenChange={setShowReasonModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Informe o motivo/solução</DialogTitle>
            </DialogHeader>
            <Input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Descreva o motivo ou solução..."
              autoFocus
            />
            <DialogFooter>
              <Button
                onClick={async () => {
                  if (!reason.trim()) return alert('Informe o motivo ou solução!');
                  await onAssign(pendingTicket.id, pendingStatus, reason);
                  setShowReasonModal(false);
                  setPendingStatus(null);
                  setPendingTicket(null);
                  setReason('');
                }}
              >Salvar</Button>
              <DialogClose asChild>
                <Button variant="ghost" onClick={() => { setShowReasonModal(false); setReason(''); setPendingStatus(null); setPendingTicket(null); }}>Cancelar</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}